import {axiosInstance} from '~/utils/axios-accessor'
import {AUTH_EVENT, B2B_AUTH_EVENT, EventBus, RESET, SWITCH_LANGUAGE} from '~/utils/event-bus'
import {registeredPerson as blankRegisteredPerson} from '@/utils/auth-blank-states'
import UserFactory from '@/models/User/UserFactory'
import {appInstance} from '@/utils/app-accessor'
import {runtimeStore} from '@/composables/store-accessor'
import {defineStore} from 'pinia'
import type {Ref} from 'vue'

//TODO Preferably change it in API
export function authorizationPersonRsTransformer(person: any) {
    person.firstName = person.name
    person.lastName = person.surname
    person.personId = person.id
    delete person.name
    delete person.surname
    delete person.id
    if (person.memberLevel === 1) {
        person.memberLevel = 'GUEST'
    } else if (person.memberLevel === 3) {
        person.memberLevel = 'REGISTERED'
    }
    person.password = null
    return person
}

export const useAuthStore = defineStore('auth', () => {
    const person: Ref = ref(null)
    const registeredPerson: Ref = ref(blankRegisteredPerson())
    const company: Ref = ref({})
    const companyType: Ref = ref('')
    const availableProducts: Ref<any[]> = ref([])
    const systemCurrency: Ref = ref(null)
    const userTimeZone: Ref = ref(null)
    const settings: Ref = ref(null)

    const route = useRoute()

    function SET_REGISTERED_PERSON(_person: any) {
        registeredPerson.value = _person
    }

    function SET_REGISTERED_PERSON_PROP(data: any) {
        registeredPerson.value[data.prop] = data.val
    }

    function SET_PERSON(_person: any) {
        person.value = _person
    }

    function SET_COMPANY_TYPE(_companyType: any) {
        companyType.value = _companyType
    }

    function SET_AVAILABLE_PRODUCTS(_availableProducts: any) {
        availableProducts.value = _availableProducts
    }

    function SET_SYSTEM_CURRENCY(_systemCurrency: any) {
        systemCurrency.value = _systemCurrency
    }

    function SET_USER_TIMEZONE(_userTimeZone: any) {
        userTimeZone.value = _userTimeZone
    }

    function logOut() {
        if (import.meta.client) useCookie('token').value = null

        EventBus.$emit(RESET)

        person.value = null
        companyType.value = null
        registeredPerson.value = blankRegisteredPerson()
        company.value = {}
        availableProducts.value = []
        systemCurrency.value = null
        userTimeZone.value = null
        settings.value = null
    }

    async function loadCompany() {
        try {
            const rs = await appInstance.$api.companies.get({relationship: 'OWN'})
            company.value = rs.companies[0]
        } catch (e) {
            company.value = {}
        }
    }

    async function loadPerson(id: any) {
        try {
            const personRs = await appInstance.$api.persons.get({id}),
                _person = personRs.users[0]
            _person.firstName = _person.firstName || _person.firstNameLocalized
            _person.lastName = _person.lastName || _person.lastNameLocalized

            SET_PERSON(_person)
        } catch (e) {
            SET_PERSON(null)
        }
    }

    function updatePerson(personData: any) {
        person.value = Object.assign({}, person.value, personData)
    }

    function authUser(rs: any) {
        if (rs.person) {
            rs.person = authorizationPersonRsTransformer(rs.person)
            rs.person.role = 'client'
        }

        SET_PERSON(rs.person)
        SET_COMPANY_TYPE(null)

        axiosInstance.setHeader('Token', rs.token)
        if (import.meta.client)
            useCookie('token', {
                secure: process.env.NODE_ENV !== 'development' && !appInstance.$conf.public.disableSecureCookie,
            }).value = rs.token

        SET_USER_TIMEZONE(null)

        if (rs.person) {
            EventBus.$emit(AUTH_EVENT)
        }

        return rs.token
    }

    async function register({prefix, firstName, lastName, contactPhone, email, password, memberLevel, language}) {
        runtimeStore.SET_AUTH_ACTIVE(true)
        try {
            await appInstance.$api.registration.post({
                prefix,
                firstName,
                lastName,
                contactPhone,
                email,
                password,
                memberLevel,
                language,
            })
            const rs = await appInstance.$api.authorization.get({login: email, password})
            authUser(rs)

            if (Object.values(appInstance.$conf.public.locales).includes(rs.language)) {
                EventBus.$emit(SWITCH_LANGUAGE, rs.language)
            }
        } finally {
            runtimeStore.SET_AUTH_ACTIVE(false)
        }
    }

    async function auth(rq?: any) {
        runtimeStore.SET_AUTH_ACTIVE(true)
        try {
            const rs = await appInstance.$api.authorization.get(rq)
            if (!route.name?.includes('Booking') && rq) {
                EventBus.$emit(RESET)
            }
            if (route.name?.includes('Confirmation') && rq) {
                await appInstance.$router.push({name: 'home'})
            }
            const {availableProducts: _availableProducts, systemCurrency: _systemCurrency} = rs.settings
            if (_availableProducts) {
                SET_AVAILABLE_PRODUCTS(_availableProducts)
            }
            if (_systemCurrency) {
                SET_SYSTEM_CURRENCY(_systemCurrency)
            }
            if (Object.values(appInstance.$conf.public.locales).includes(rs.language)) {
                EventBus.$emit(SWITCH_LANGUAGE, rs.language)
            }

            //TODO Preferably change it in API
            return authUser(rs)
        } finally {
            runtimeStore.SET_AUTH_ACTIVE(false)
        }
    }

    async function authB2B(rq: any) {
        runtimeStore.SET_AUTH_ACTIVE(true)
        try {
            const rs = await appInstance.$api.authorization.get(rq)
            settings.value = rs
            await setB2B(rs)
        } finally {
            runtimeStore.SET_AUTH_ACTIVE(false)
        }
    }

    async function setB2B({
        token,
        companyType,
        userId,
        availableProducts,
        userTimeZone,
        systemCurrency,
        language,
        contractCurrency,
    }: any) {
        axiosInstance.setHeader('Token', token)
        await loadPerson(userId)
        SET_COMPANY_TYPE(companyType)
        SET_AVAILABLE_PRODUCTS(availableProducts)
        SET_USER_TIMEZONE(userTimeZone)

        if (import.meta.client)
            useCookie('token', {
                secure: process.env.NODE_ENV !== 'development' && !appInstance.$conf.public.disableSecureCookie,
            }).value = token
        EventBus.$emit(B2B_AUTH_EVENT)
        SET_REGISTERED_PERSON(blankRegisteredPerson())
        if (contractCurrency) {
            persistentStore.SET_CURRENCY(contractCurrency)
        }
        if (systemCurrency) {
            SET_SYSTEM_CURRENCY(systemCurrency)
        }
        EventBus.$emit(AUTH_EVENT)
        await loadCompany()
        if (Object.values(appInstance.$conf.public.locales).includes(language)) {
            EventBus.$emit(SWITCH_LANGUAGE, language)
        }
    }

    const user = computed(() => UserFactory.create(companyType.value, person.value))

    const rolePrefix = computed(() => {
        const role = person.value?.role
        if (!role) {
            return null
        }
        const split = role.split('.')
        return split.length === 2 ? split[0] : null
    })

    const personRole = computed(() => {
        if (!person.value?.role) return null
        const indexOfFirstDot = person.value.role.indexOf('.')
        return indexOfFirstDot ? person.value.role.substring(indexOfFirstDot + 1) : null
    })

    const orderType = computed(() => {
        if (companyType.value === 'INTERNAL_SUPPLIER') return 'SUPPLIER'
        return ['TOUR_OPERATOR', 'TOUR_OPERATOR_1LEVEL'].includes(companyType.value) ? 'TO' : 'TA'
    })

    const isGuest = computed(() => !person.value)

    const isB2C = computed(() => !companyType.value)

    const isB2B = computed(() => isTourOperator.value || isCorporate.value || isAgency.value || isSupplier.value)

    const isAgency = computed(() => companyType.value === 'TOUR_AGENCY')

    const isTO1 = computed(() => companyType.value === 'TOUR_OPERATOR_1LEVEL')

    const isTO2 = computed(() => companyType.value === 'TOUR_OPERATOR')

    const isTourOperator = computed(() => ['TOUR_OPERATOR', 'TOUR_OPERATOR_1LEVEL'].includes(companyType.value))

    const isCorporate = computed(() => companyType.value === 'CORPORATOR')

    const isSupplier = computed(() => companyType.value === 'INTERNAL_SUPPLIER')

    const showPaymentAndInvoiceStatus = computed(() => {
        if (personRole.value === 'dispatcher') return false

        return (
            (isB2C.value && appInstance.$conf.public.showPaymentAndInvoiceStatus.b2c) ||
            (isB2B.value && appInstance.$conf.public.showPaymentAndInvoiceStatus.b2b)
        )
    })

    const hasCorporateAccess = computed(() => {
        return (
            isB2B.value &&
            (!isCorporate.value ||
                ['corp.director', 'corp.group_supervisor', 'corp.trip_coordinator'].includes(person.value?.role))
        )
    })

    const isGuide = computed(() => person.value?.role && ['to1l.guide'].includes(person.value.role))

    const isDriver = computed(() => person.value?.role && ['to1l.driver'].includes(person.value.role))

    const isAccountant = computed(() => personRole.value === 'accountant')

    const defaultCitizenshipId = computed(() => {
        let citizenshipId

        if (route?.query?.citizenshipId) {
            citizenshipId = route.query.citizenshipId
        } else if (route?.query?.driverCitizenshipId) {
            citizenshipId = route.query.driverCitizenshipId
        } else if (route?.query?.applicantCountryId) {
            citizenshipId = route.query.applicantCountryId
        } else if (persistentStore.selectedCompanyAndAgent?.company) {
            citizenshipId =
                persistentStore.selectedCompanyAndAgent.agent?.citizenshipId ||
                persistentStore.selectedCompanyAndAgent.company.countryId
        } else if (persistentStore.selectedPrivateClient?.citizenshipId) {
            citizenshipId = persistentStore.selectedPrivateClient.citizenshipId
        } else {
            citizenshipId =
                person.value?.citizenshipId || company.value?.countryId || appInstance.$conf.public.defaultCitizenshipId
        }

        return citizenshipId ? +citizenshipId : null
    })

    async function serverInit() {
        try {
            const token = useCookie('token').value
            if (token) {
                axiosInstance.setHeader('Token', token)
                const info = await appInstance.$api.authorizationInfo.get({})
                if (info.person) {
                    const id = info.person.id || info.person.originalId
                    try {
                        const {persons} = await appInstance.$api.privateClients.get({id}),
                            _person = persons[0]
                        _person.firstName = _person.firstName || _person.firstNameLocalized
                        _person.lastName = _person.lastName || _person.lastNameLocalized
                        SET_PERSON({..._person, role: 'client'})
                    } catch {
                        await auth()
                        if (appInstance.$conf.public.onlyRegistered) {
                            appInstance.app.context.redirect({name: 'authorization'})
                        }
                    }
                    SET_SYSTEM_CURRENCY(info.settings.systemCurrency)
                } else if (info.userId) {
                    settings.value = info
                    const id = info.userId
                    await Promise.all([loadPerson(id), loadCompany()])
                    if (person.value) {
                        SET_COMPANY_TYPE(company.value.type)
                        SET_USER_TIMEZONE(info.userTimeZone)

                        SET_AVAILABLE_PRODUCTS(info.availableProducts)
                        SET_SYSTEM_CURRENCY(info.systemCurrency)
                    } else {
                        await auth()
                        if (appInstance.$conf.public.onlyRegistered) {
                            appInstance.app.context.redirect({name: 'b2b-auth'})
                        }
                    }
                }
            } else {
                const token = await auth()
                appInstance.runWithContext(() => {
                    useCookie('token', {
                        secure: process.env.NODE_ENV !== 'development' && !appInstance.$conf.public.disableSecureCookie,
                    }).value = token
                })
            }
        } catch (e) {
            console.error(e)
            appInstance.runWithContext(() => {
                useCookie('token').value = null
            })
            throw createError({
                statusCode: 500,
                message: appInstance.$i18n.t('error_message.service_unavailable'),
            })
        }
    }

    return {
        person,
        registeredPerson,
        company,
        companyType,
        availableProducts,
        systemCurrency,
        userTimeZone,
        SET_REGISTERED_PERSON,
        SET_REGISTERED_PERSON_PROP,
        SET_PERSON,
        SET_COMPANY_TYPE,
        SET_AVAILABLE_PRODUCTS,
        SET_SYSTEM_CURRENCY,
        SET_USER_TIMEZONE,
        logOut,
        loadCompany,
        loadPerson,
        updatePerson,
        authUser,
        register,
        auth,
        authB2B,
        setB2B,
        user,
        rolePrefix,
        personRole,
        orderType,
        isGuest,
        isB2C,
        isB2B,
        isAgency,
        isTO1,
        isTO2,
        isTourOperator,
        isCorporate,
        isSupplier,
        showPaymentAndInvoiceStatus,
        hasCorporateAccess,
        isGuide,
        isDriver,
        isAccountant,
        serverInit,
        settings,
        defaultCitizenshipId,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
